import { useEffect, type CSSProperties } from "react";
import { useStore } from "../store/globalState";
import { playAmountMismatchAlert } from "../utils/playAmountMismatchAlert";
import "./Toast.css";

/** Dev strict mode mounts twice. Skip the second chime. */
let lastChimeAt = 0;

/** CSS `border-color` cannot paint a gradient. Partner buttons often are one. */
function solidFill(value: string): string {
  const hex = value.match(
    /#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\b/,
  );
  return hex?.[0] ?? value;
}

export interface ToastProps {
  message: string;
  duration?: number;
  type?: "success" | "error" | "info";
  /** Same two-tone chime as the cash-deposit amount check. */
  sound?: boolean;
  onClose?: () => void;
}

export const Toast = ({
  message,
  duration = 2000,
  type = "success",
  sound = false,
  onClose,
}: ToastProps) => {
  const { backgroundBtn, bodyCopy } = useStore().theme.whiteView;
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  useEffect(() => {
    if (!sound) return;
    const now = Date.now();
    if (now - lastChimeAt < 500) return;
    lastChimeAt = now;
    playAmountMismatchAlert();
  }, [sound]);

  return (
    <div
      className={`toast toast-${type}`}
      role="status"
      style={
        {
          "--toast-accent": solidFill(backgroundBtn),
          "--toast-ink": solidFill(bodyCopy),
        } as CSSProperties
      }
    >
      {type === "success" && <span className="toast-icon">✓</span>}
      {type === "error" && <span className="toast-icon">✕</span>}
      <span className="toast-message">{message}</span>
    </div>
  );
};
